<script>
	import thumb_u2ILNHzT8FM from '$lib/Components/Youtube/images/u2ILNHzT8FM.jpg';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import howCityNoiseAffectsYourHealth from './images/howCityNoiseAffectsYourHealth.jpg';
	import manInstallingWindowInserts from './images/manInstallingWindowInserts.jpg';
	import noiseBadForStressLevels from './images/noiseBadForStressLevels.jpg';
	import noiseComplaintNewYorkCity from './images/noiseComplaintNewYorkCity.jpg';
	import nycOpenData from './images/nycOpenData.png';
</script>

<Section>
	<p>
		You’re not imagining it—everything is getting louder. The population’s
		growing, and with it, conversations, transportation, and devices. Devices
		need power and data storage facilities which whir in the background.
	</p>
	<p>And at night, they need to party.</p>
	<Image
		src={noiseBadForStressLevels}
		alt="crowded street that causes noise complaints in nyc"
	/>
	<p>
		Noise is bad for our stress levels and for health. Our bodies were not made
		for this much noise. Neither were our buildings, which is why quiet places
		are disappearing at a rate far faster than any species according to
		ecologist <a
			href="https://www.theatlantic.com/magazine/archive/2019/11/the-end-of-silence/598366"
			>Gordon Hempton</a
		>.
	</p>
	<Bubble>
		{#snippet titleSlot()}
			<p>
				We feel it in our bones. Over 99.8 billion (yes, billion) NYC noise
				complaints were made in 2019 alone. Not much is being done with the
				complaints, though, unless:
			</p>
		{/snippet}
		<ul>
			<li>You know who to call</li>
			<li>You know NYC noise code</li>
			<li>You fight city noise as a community</li>
		</ul>
	</Bubble>
</Section>

<Section>
	<h2>Noise Complaints in NYC by Year</h2>
	<Image
		src={nycOpenData}
		alt="noise complaints in nyc by year"
		class="w-full"
	/>
	<p>
		Source: <a
			href="https://data.cityofnewyork.us/Social-Services/311-Noise-Complaints/p5f6-bkga"
			target="_blank"
			rel="external">NYC Open Data</a
		>
	</p>
</Section>

<Section>
	<h2>When & Where to File a Noise Complaint in NYC</h2>
	<p class="text-sm italic">
		Disclaimer: If the noise you hear sounds like an emergency, call 911.
	</p>
	<p>
		If it’s a tenant issue, like a noisy neighbor, it likely must be handled by
		your landlord or management company. That’s what anyone responding to a
		neighborly noise complaint will tell you when you call.
	</p>
	<p>
		For other city noise, make sure you contact the correct department.
		Complaints about a barking dog are handled by a different department than
		construction noise. 311 can direct you, but you’ll be better served if you
		get there yourself. Here’s a list of all <a
			href="https://portal.311.nyc.gov/category/?id=311-12"
			target="_blank"
			rel="external">NYC noise complaint categories</a
		>
		with links to register a noise complaint online. Here are instructions for
		<a href="/resources/blog/how-to-file-noise-complaint-online"
			>how to file a noise complaint anonymously</a
		>.
	</p>
	<p>
		NYC noise code got its first update in 30 years in 2007. So, to say it’s not
		keeping up with the times is an understatement. An individual’s ability to
		create amplified noise has increased significantly in just the last 13
		years. The way NYC handles noise complaints is that both the New York City
		Department of Environmental Protection (DEP) and New York City Police
		Department (NYPD) share the responsibility of reducing “excessive and
		unreasonable and prohibited noises”.
	</p>
	<p>
		So, what is excessive? They measure this by sound level (dBA), distance from
		sound, time of day, and duration. These change depending on the offending
		noise and its source.
	</p>
	<Bubble>
		{#snippet titleSlot()}
			<p class="font-bold">A commercial space in NYC cannot exceed</p>
		{/snippet}
		<ul>
			<li>42 dBA</li>
			<li>As measured from nearby residences</li>
			<li>From 10 pm to 7 am</li>
		</ul>
	</Bubble>
	<p>
		But animal noises have a duration limit included that changes with the time
		of day so it’s much shorter at night. All NYC noise code specifics can be
		found in this <a href="/static/pdfs/noise-code-guide-summary.pdf">guide</a>.
	</p>
</Section>

<Section>
	<h2>How City Noise Affects Your Health</h2>
	<p>
		It almost seems like too much to deal with. As stated in NYPost’s article <a
			href="https://nypost.com/2018/03/04/these-nyc-neighborhoods-have-the-most-noise-complaints"
			target="_blank"
			rel="external">These NYC neighborhoods have the most noise complaints</a
		>, “The frustrated Manhattan denizens say their incessant calls for help
		have gone unanswered and they’re fed up with the city’s failure to relieve
		their aching eardrums.”
	</p>
	<Image
		src={howCityNoiseAffectsYourHealth}
		alt="city noise causes annoyance and poor health"
	/>
	<p>
		But it’s not just your ears that are at risk <a
			href="https://www.who.int/sustainable-development/transport/health-risks/noise/en"
			target="_blank"
			rel="external">World Health Organization</a
		> (WHO) says that noise exposure is responsible for increased risk of heart disease,
		cognitive impairment in children, stress-related mental health risks, sleep disturbance,
		as well as tinnitus. In fact, the federal government created the 1972 Noise Control
		act with knowledge of the dangers of noise pollution, citing its effect on our
		health and welfare.
	</p>
	<p>
		New Yorkers are especially vulnerable with higher average daily noise
		exposure. NYC city noise averages 85 decibels where WHO’s standard for
		daytime noise is 55 decibels.
	</p>
</Section>

<Section>
	<h2>Take Action Against Noise in Your Home</h2>
	<Youtube posterImageSrc={thumb_u2ILNHzT8FM} videoId="u2ILNHzT8FM" />
	<p>
		Protect yourself and your family from city noise. Wear earplugs during loud
		events and noise-canceling headphones when in loud neighborhoods.
	</p>
	<p>
		Soundproof your home by adding thick rugs, art to your walls, and plush
		furniture to absorb noise. Take it a bit further and add <a
			href="https://www.audimute.com/acousticolor-acoustic-shapes"
			target="_blank"
			rel="external">acoustic wall panels</a
		>
		– some are actually very chic now. Soundproofing windows in NYC is possible with
		window inserts, which don’t permanently alter your windows and can block up to
		70% of outside noise.
	</p>
	<Image
		alt="man installing window inserts to block city noise"
		src={manInstallingWindowInserts}
	/>
	<p>
		Consider the noise factor when buying home appliances. Some modern
		appliances list decibel levels, or you can ask the manufacturer. Reading
		reviews, especially those from bloggers with small children or noise
		sensitivities, can help with research.
	</p>
	<p>
		These soundproofing options are fantastic steps in creating more quiet in
		your life. But they can also be isolating and put all the responsibility on
		the individual. How can individuals come together to create real change?
	</p>
</Section>

<Section>
	<h2>Take Action Together Beyond Noise Complaints in NYC</h2>
	<p>
		Talk to those in your community. As awareness spreads, so does power. The
		more information you have and share, the more you can hold the responsible
		parties accountable.
	</p>
	<p>
		Find existing community action groups. <a
			href="https://wp.nyu.edu/sonyc"
			target="_blank"
			rel="external">SONYC</a
		>
		gathers city noise throughout NYC with the hope that AI can learn to identify
		specific sounds on its own. They have millions of recordings and participating
		scientists identify and tag the sound (including whether it is actionable according
		to NYC noise code). SONYC is currently
		<a
			href="https://www.zooniverse.org/projects/anaelisa24/sounds-of-new-york-city-sonyc"
			target="_blank"
			rel="external">looking for citizen scientists</a
		> for this project.
	</p>
	<p>
		A citizen scientist could be a construction worker who can tell the
		difference between the sounds of heavy equipment in action. Or, a citizen
		scientist could be someone who can discern the difference between a person
		shouting and music blasting from a car. Joining their ranks will help put in
		effect a better system of analyzing sound and NYC noise complaints so
		officials can better perform their job.
	</p>
	<Image
		alt="nyc skyline where citizen scientists track sounds to better assess noise complaints in nyc"
		src={noiseComplaintNewYorkCity}
	/>
	<p>
		Filing noise complaints in NYC is one drop in the bucket. Using community
		action like SONYC or finding others who suffer from similar issues
		strengthens your voice. Karthic Thallikar filed noise complaints about a
		sound he thought only he could hear. He wrote letters to the source once he
		found it. Called the police. It was all ignored.
	</p>
	<p>
		It wasn’t until 120 members of his community <a
			href="https://www.theatlantic.com/magazine/archive/2019/11/the-end-of-silence/598366"
			target="_blank"
			rel="external">formed a noise coalition</a
		> that the complaints were taken seriously. The company that produced the noise
		eventually hired their own acoustic consultant and decided to spend $2 million
		on soundproofing. Next on the noise coalition’s list, taking their issues to
		the city planners so their peace isn’t disturbed again.
	</p>
</Section>

<GetAFreeEstimate />
